package com.iptvplayer.shared.data.remote.xtream

import com.iptvplayer.shared.domain.model.Channel
import com.iptvplayer.shared.domain.model.EpgProgram
import com.iptvplayer.shared.domain.model.Episode
import com.iptvplayer.shared.domain.model.Season
import com.iptvplayer.shared.domain.model.SeriesInfo
import com.iptvplayer.shared.domain.model.SeriesItem
import com.iptvplayer.shared.domain.model.StreamType
import com.iptvplayer.shared.domain.model.VodItem
import com.iptvplayer.shared.data.remote.ProviderHttpException
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.http.isSuccess
import kotlin.io.encoding.Base64
import kotlin.io.encoding.ExperimentalEncodingApi

class XtreamApiClient(private val client: HttpClient) {

    /**
     * GET player_api.php, failing with a status-aware [ProviderHttpException]
     * instead of letting `.body()` throw a confusing deserialization error when
     * the panel is down (e.g. a Cloudflare "error code: 502" text page).
     */
    private suspend inline fun <reified T> playerApi(
        serverUrl: String,
        username: String,
        password: String,
        action: String? = null,
        categoryId: String? = null,
        seriesId: Int? = null,
        streamId: Int? = null,
        limit: Int? = null
    ): T {
        val response = client.get("$serverUrl/player_api.php") {
            parameter("username", username)
            parameter("password", password)
            action?.let { parameter("action", it) }
            categoryId?.let { parameter("category_id", it) }
            seriesId?.let { parameter("series_id", it.toString()) }
            streamId?.let { parameter("stream_id", it.toString()) }
            limit?.let { parameter("limit", it.toString()) }
        }
        if (!response.status.isSuccess()) {
            throw ProviderHttpException(response.status.value)
        }
        return response.body()
    }

    suspend fun authenticate(
        serverUrl: String,
        username: String,
        password: String
    ): XtreamAuthResponse = playerApi(serverUrl, username, password)

    suspend fun getLiveCategories(
        serverUrl: String,
        username: String,
        password: String
    ): List<XtreamCategory> = playerApi(serverUrl, username, password, "get_live_categories")

    suspend fun getLiveStreams(
        serverUrl: String,
        username: String,
        password: String,
        categoryId: String? = null
    ): List<XtreamLiveStream> =
        playerApi(serverUrl, username, password, "get_live_streams", categoryId)

    suspend fun getVodCategories(
        serverUrl: String,
        username: String,
        password: String
    ): List<XtreamCategory> = playerApi(serverUrl, username, password, "get_vod_categories")

    suspend fun getVodStreams(
        serverUrl: String,
        username: String,
        password: String,
        categoryId: String? = null
    ): List<XtreamVodStream> =
        playerApi(serverUrl, username, password, "get_vod_streams", categoryId)

    suspend fun getSeriesCategories(
        serverUrl: String,
        username: String,
        password: String
    ): List<XtreamCategory> = playerApi(serverUrl, username, password, "get_series_categories")

    suspend fun getSeries(
        serverUrl: String,
        username: String,
        password: String,
        categoryId: String? = null
    ): List<XtreamSeries> = playerApi(serverUrl, username, password, "get_series", categoryId)

    suspend fun getSeriesInfo(
        serverUrl: String,
        username: String,
        password: String,
        seriesId: Int
    ): XtreamSeriesInfoResponse =
        playerApi(serverUrl, username, password, "get_series_info", seriesId = seriesId)

    /**
     * Short EPG (now/next) for one live stream. Titles/descriptions come back
     * base64-encoded — see [mapShortEpg].
     */
    suspend fun getShortEpg(
        serverUrl: String,
        username: String,
        password: String,
        streamId: Int,
        limit: Int = 2
    ): XtreamShortEpgResponse =
        playerApi(serverUrl, username, password, "get_short_epg", streamId = streamId, limit = limit)

    /** Decode the raw short-EPG listings into domain [EpgProgram]s (sorted by start). */
    fun mapShortEpg(response: XtreamShortEpgResponse): List<EpgProgram> =
        response.epgListings.mapNotNull { listing ->
            val startSec = listing.startTimestamp.toLongOrNull() ?: return@mapNotNull null
            val stopSec = listing.stopTimestamp.toLongOrNull() ?: return@mapNotNull null
            val title = decodeBase64Text(listing.title)
            if (title.isBlank()) return@mapNotNull null
            EpgProgram(
                title = title,
                description = decodeBase64Text(listing.description),
                startMs = startSec * 1000,
                endMs = stopSec * 1000
            )
        }.sortedBy { it.startMs }

    /**
     * Xtream base64-encodes EPG text; a few panels send plain text instead.
     * Malformed base64 (or non-UTF-8 payloads) falls back to the raw string.
     */
    @OptIn(ExperimentalEncodingApi::class)
    private fun decodeBase64Text(value: String): String {
        val trimmed = value.trim()
        if (trimmed.isEmpty()) return trimmed
        return try {
            Base64.decode(trimmed).decodeToString().trim()
        } catch (_: Exception) {
            trimmed
        }
    }

    // ──────────────────── URL Builders ────────────────────

    fun buildLiveStreamUrl(
        serverUrl: String,
        username: String,
        password: String,
        streamId: Int
    ): String = "$serverUrl/live/$username/$password/$streamId.ts"

    fun buildVodStreamUrl(
        serverUrl: String,
        username: String,
        password: String,
        streamId: Int,
        containerExtension: String? = null
    ): String {
        val ext = containerExtension ?: "mp4"
        return "$serverUrl/movie/$username/$password/$streamId.$ext"
    }

    fun buildEpisodeStreamUrl(
        serverUrl: String,
        username: String,
        password: String,
        episodeId: String,
        containerExtension: String? = null
    ): String {
        val ext = containerExtension ?: "mp4"
        return "$serverUrl/series/$username/$password/$episodeId.$ext"
    }

    // ──────────────────── Domain Mappers ────────────────────

    fun mapLiveStreamToChannel(
        stream: XtreamLiveStream,
        sourceId: String,
        serverUrl: String,
        username: String,
        password: String
    ): Channel = Channel(
        id = "${sourceId}_live_${stream.streamId}",
        sourceId = sourceId,
        name = stream.name,
        streamUrl = buildLiveStreamUrl(serverUrl, username, password, stream.streamId),
        logoUrl = stream.streamIcon,
        group = stream.categoryName ?: stream.categoryId,
        streamType = StreamType.MPEGTS
    )

    fun mapVodStreamToVodItem(
        stream: XtreamVodStream,
        sourceId: String,
        serverUrl: String,
        username: String,
        password: String
    ): VodItem = VodItem(
        id = "${sourceId}_vod_${stream.streamId}",
        sourceId = sourceId,
        name = stream.name,
        streamUrl = buildVodStreamUrl(serverUrl, username, password, stream.streamId, stream.containerExtension),
        coverUrl = stream.streamIcon,
        plot = stream.plot,
        genre = stream.genre ?: stream.categoryName,
        duration = stream.duration?.toLongOrNull(),
        rating = stream.rating?.toDoubleOrNull()
            ?: stream.rating5based?.toDoubleOrNull()?.let { it * 2.0 },
        group = stream.categoryName ?: stream.categoryId
    )

    fun mapLiveStreamsToChannels(
        streams: List<XtreamLiveStream>,
        sourceId: String,
        serverUrl: String,
        username: String,
        password: String
    ): List<Channel> = streams.map { mapLiveStreamToChannel(it, sourceId, serverUrl, username, password) }

    fun mapVodStreamsToVodItems(
        streams: List<XtreamVodStream>,
        sourceId: String,
        serverUrl: String,
        username: String,
        password: String
    ): List<VodItem> = streams.map { mapVodStreamToVodItem(it, sourceId, serverUrl, username, password) }

    fun mapSeriesToItem(series: XtreamSeries, sourceId: String): SeriesItem = SeriesItem(
        id = "${sourceId}_series_${series.seriesId}",
        sourceId = sourceId,
        seriesId = series.seriesId,
        name = series.name,
        posterUrl = series.cover,
        plot = series.plot,
        cast = series.cast,
        director = series.director,
        genre = series.genre ?: series.categoryName,
        releaseDate = series.releaseDate,
        rating = series.rating?.toDoubleOrNull(),
        group = series.categoryName ?: series.categoryId
    )

    fun mapSeriesListToItems(series: List<XtreamSeries>, sourceId: String): List<SeriesItem> =
        series.map { mapSeriesToItem(it, sourceId) }

    /** Build the full domain [SeriesInfo] (seasons + episodes with playable URLs). */
    fun mapSeriesInfo(
        item: SeriesItem,
        response: XtreamSeriesInfoResponse,
        serverUrl: String,
        username: String,
        password: String
    ): SeriesInfo {
        // Group seasons by number; sort by number; pick metadata from XtreamSeasonInfo when present.
        val episodesBySeason = response.episodes.mapKeys { it.key.toIntOrNull() ?: 0 }
        val seasonNumbers = (response.seasons.map { it.seasonNumber } + episodesBySeason.keys)
            .filter { it > 0 }.distinct().sorted()
        val seasonMetaByNumber = response.seasons.associateBy { it.seasonNumber }

        val seasons = seasonNumbers.map { num ->
            val meta = seasonMetaByNumber[num]
            val rawEpisodes = episodesBySeason[num].orEmpty()
            Season(
                seasonNumber = num,
                name = meta?.name,
                overview = meta?.overview,
                coverUrl = meta?.coverBig,
                episodes = rawEpisodes
                    .sortedBy { it.episodeNum.toIntOrNull() ?: 0 }
                    .map { ep ->
                        Episode(
                            id = "${item.id}_s${num}_e${ep.episodeNum}",
                            seriesId = item.seriesId,
                            seasonNumber = num,
                            episodeNumber = ep.episodeNum.toIntOrNull() ?: 0,
                            title = ep.title.ifBlank { "Episode ${ep.episodeNum}" },
                            streamUrl = buildEpisodeStreamUrl(serverUrl, username, password, ep.id, ep.containerExtension),
                            plot = ep.info.plot,
                            durationSec = ep.info.durationSecs,
                            coverUrl = ep.info.movieImage,
                        )
                    }
            )
        }
        return SeriesInfo(series = item, seasons = seasons)
    }
}
